import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/form.css';
import api, { asArray, asObject } from '../lib/api';

function AnalisarProjeto() {
  const navigate = useNavigate();
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingDwg, setUploadingDwg] = useState(false);
  const [compartimentos, setCompartimentos] = useState([
    { nome: '', area: 0, pe_direito: 2.7, iluminacao_abertura: 0, ventilacao_abertura: 0, tipo: 'permanencia_prolongada' }
  ]);

  const [form, setForm] = useState({
    endereco: '',
    bairro: '',
    zona_uso_solo: 'URB1',
    inscricao_imobiliaria: '',
    area_lote: 0,
    testada: 0,
    profundidade: 0,
    tipo_edificacao: 'residencial_uni',
    atividade_cnae: '',
    numero_pavimentos: 1,
    numero_unidades: 1,
    numero_comodos: 1,
    altura_edificacao: 0,
    area_construida_total: 0,
    area_projecao: 0,
    area_impermeavel: 0,
    recuo_frontal: 0,
    recuo_lateral_esq: 0,
    recuo_lateral_dir: 0,
    recuo_fundos: 0,
    vagas_estacionamento: 0,
    vagas_pcd: 0,
    tem_rampa_acesso: false,
    banheiro_pcd: false,
    elevador: false,
    area_para_bombeiros: 0,
    ocupacao_bombeiros: ''
  });

  useEffect(() => {
    // Carregar zonas
    api.get('/api/tabelas/zonas')
      .then(res => setZonas(asArray(res.data)))
      .catch(err => console.error('Erro ao carregar zonas:', err));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : isNaN(value) ? value : parseFloat(value) || ''
    });
  };

  const handleCompartimentoChange = (index, field, value) => {
    const novoCompartimentos = [...compartimentos];
    novoCompartimentos[index] = {
      ...novoCompartimentos[index],
      [field]: isNaN(value) ? value : parseFloat(value) || 0
    };
    setCompartimentos(novoCompartimentos);
  };

  const adicionarCompartimento = () => {
    setCompartimentos([
      ...compartimentos,
      { nome: '', area: 0, pe_direito: 2.7, iluminacao_abertura: 0, ventilacao_abertura: 0, tipo: 'permanencia_prolongada' }
    ]);
  };

  const removerCompartimento = (index) => {
    setCompartimentos(compartimentos.filter((_, i) => i !== index));
  };

  const handleDwgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDwg(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/dxf/api/v1/parse-dxf', formData);

      // Auto-fill form fields from DWG data
      const data = asObject(response.data) || {};
      const projetoExtraido = asObject(data.projeto_extraido) || data;
      if (projetoExtraido.area_lote) setForm(f => ({ ...f, area_lote: projetoExtraido.area_lote }));
      if (projetoExtraido.testada) setForm(f => ({ ...f, testada: projetoExtraido.testada }));
      if (projetoExtraido.profundidade) setForm(f => ({ ...f, profundidade: projetoExtraido.profundidade }));
      if (projetoExtraido.area_construida_total) setForm(f => ({ ...f, area_construida_total: projetoExtraido.area_construida_total }));
      if (projetoExtraido.area_projecao) setForm(f => ({ ...f, area_projecao: projetoExtraido.area_projecao }));
      if (projetoExtraido.zona_uso_solo) setForm(f => ({ ...f, zona_uso_solo: projetoExtraido.zona_uso_solo }));

      alert('Arquivo DWG importado com sucesso! Os dados foram preenchidos automaticamente.');
    } catch (err) {
      setError('Erro ao importar arquivo DWG: ' + (err.response?.data?.detail || err.message));
      console.error('Erro:', err);
    } finally {
      setUploadingDwg(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        compartimentos: compartimentos.filter(c => c.nome) // Filtrar compartimentos vazios
      };

      const response = await api.post('/api/analisar', payload);
      const data = asObject(response.data);
      if (!data?.relatorio_id) {
        throw new Error('Resposta inválida da API de análise');
      }
      const relatorioId = data.relatorio_id;
      navigate(`/resultados/${relatorioId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao analisar projeto');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <h1>📋 Analisar Projeto de Construção</h1>
        <p className="subtitle">Preencha os dados do seu projeto para análise automática de compliance urbanístico</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Seção: Upload DWG */}
          <section className="form-section dwg-section">
            <h2>📄 Importar Arquivo DWG</h2>
            <p className="section-description">Opcionalmente, envie o arquivo DWG/DXF do projeto para extrair dimensões automaticamente</p>
            <div className="dwg-upload-box">
              <input
                type="file"
                id="dwg-upload"
                accept=".dwg,.dxf,.DWG,.DXF"
                onChange={handleDwgUpload}
                disabled={uploadingDwg}
              />
              <label htmlFor="dwg-upload" className="dwg-upload-label">
                {uploadingDwg ? '⏳ Processando arquivo...' : '📥 Selecione arquivo DWG/DXF'}
              </label>
            </div>
          </section>

          {/* Seção 1: Dados Básicos */}
          <section className="form-section">
            <h2>🏠 Dados Básicos</h2>
            <p className="section-description">Informações gerais do imóvel e localização no município</p>
            <div className="form-grid">
              <div className="form-group">
                <input
                  type="text"
                  name="endereco"
                  placeholder="Ex: Rua das Flores, 123"
                  value={form.endereco}
                  onChange={handleInputChange}
                  required
                />
                <small>Endereço completo do imóvel</small>
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="bairro"
                  placeholder="Ex: Centro, Vila Industrial"
                  value={form.bairro}
                  onChange={handleInputChange}
                />
                <small>Bairro onde se localiza o imóvel</small>
              </div>
              <div className="form-group">
                <select
                  name="zona_uso_solo"
                  value={form.zona_uso_solo}
                  onChange={handleInputChange}
                >
                  <option value="">Selecione a zona</option>
                  {zonas.map(z => (
                    <option key={z.zona} value={z.zona}>{z.zona}</option>
                  ))}
                </select>
                <small>Zona de zoneamento conforme código de obras</small>
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="inscricao_imobiliaria"
                  placeholder="Ex: 12345678901234"
                  value={form.inscricao_imobiliaria}
                  onChange={handleInputChange}
                />
                <small>Número da inscrição imobiliária (IPTU)</small>
              </div>
            </div>
          </section>

          {/* Seção 2: Área e Dimensões */}
          <section className="form-section">
            <h2>📏 Área e Dimensões</h2>
            <p className="section-description">Dimensões do terreno e da edificação. Pode ser preenchido automaticamente via arquivo DWG</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Área do lote (m²)</label>
                <input
                  type="number"
                  name="area_lote"
                  placeholder="Ex: 500.00"
                  value={form.area_lote}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Área total do terreno em metros quadrados</small>
              </div>
              <div className="form-group">
                <label>Testada/Frente (m)</label>
                <input
                  type="number"
                  name="testada"
                  placeholder="Ex: 20.50"
                  value={form.testada}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Comprimento da frente do terreno (voltada para rua)</small>
              </div>
              <div className="form-group">
                <label>Profundidade/Fundo (m)</label>
                <input
                  type="number"
                  name="profundidade"
                  placeholder="Ex: 24.30"
                  value={form.profundidade}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Comprimento da profundidade do terreno (de frente a fundo)</small>
              </div>
              <div className="form-group">
                <label>Altura da edificação (m)</label>
                <input
                  type="number"
                  name="altura_edificacao"
                  placeholder="Ex: 12.50"
                  value={form.altura_edificacao}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Altura total do prédio do térreo até o topo</small>
              </div>
              <div className="form-group">
                <label>Área construída total (m²)</label>
                <input
                  type="number"
                  name="area_construida_total"
                  placeholder="Ex: 320.75"
                  value={form.area_construida_total}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Soma das áreas construídas em todos os pavimentos</small>
              </div>
              <div className="form-group">
                <label>Área de projeção (m²)</label>
                <input
                  type="number"
                  name="area_projecao"
                  placeholder="Ex: 280.50"
                  value={form.area_projecao}
                  onChange={handleInputChange}
                  step="0.01"
                />
                <small>Projeção ortogonal da edificação sobre o terreno</small>
              </div>
            </div>
          </section>

          {/* Seção 3: Recuos */}
          <section className="form-section">
            <h2>📐 Recuos da Edificação</h2>
            <p className="section-description">Distância da edificação até os limites do terreno (conforme exigências de zoneamento)</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Recuo Frontal (m)</label>
                <input
                  type="number"
                  name="recuo_frontal"
                  placeholder="Ex: 5.00"
                  value={form.recuo_frontal}
                  onChange={handleInputChange}
                  step="0.1"
                />
                <small>Distância da frente da edificação até a rua</small>
              </div>
              <div className="form-group">
                <label>Recuo Lateral Esquerdo (m)</label>
                <input
                  type="number"
                  name="recuo_lateral_esq"
                  placeholder="Ex: 1.50"
                  value={form.recuo_lateral_esq}
                  onChange={handleInputChange}
                  step="0.1"
                />
                <small>Distância do lado esquerdo até o divisa</small>
              </div>
              <div className="form-group">
                <label>Recuo Lateral Direito (m)</label>
                <input
                  type="number"
                  name="recuo_lateral_dir"
                  placeholder="Ex: 1.50"
                  value={form.recuo_lateral_dir}
                  onChange={handleInputChange}
                  step="0.1"
                />
                <small>Distância do lado direito até o divisa</small>
              </div>
              <div className="form-group">
                <label>Recuo de Fundos (m)</label>
                <input
                  type="number"
                  name="recuo_fundos"
                  placeholder="Ex: 3.00"
                  value={form.recuo_fundos}
                  onChange={handleInputChange}
                  step="0.1"
                />
                <small>Distância do fundo da edificação até o fundo do lote</small>
              </div>
            </div>
          </section>

          {/* Seção 4: Tipo e Pavimentos */}
          <section className="form-section">
            <h2>🏢 Tipo de Edificação</h2>
            <p className="section-description">Classificação, número de pavimentos e unidades do projeto</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Tipo de Edificação</label>
                <select
                  name="tipo_edificacao"
                  value={form.tipo_edificacao}
                  onChange={handleInputChange}
                >
                  <option value="residencial_uni">Residencial Unifamiliar (1 habitação)</option>
                  <option value="residencial_multi">Residencial Multifamiliar (múltiplos apts)</option>
                  <option value="comercial">Comercial (lojas, escritórios, serviços)</option>
                  <option value="misto">Misto (residencial + comercial)</option>
                  <option value="industrial">Industrial (fábrica, galpão)</option>
                </select>
                <small>Selecione a categoria principal do projeto</small>
              </div>
              <div className="form-group">
                <label>Número de Pavimentos</label>
                <input
                  type="number"
                  name="numero_pavimentos"
                  placeholder="Ex: 3"
                  value={form.numero_pavimentos}
                  onChange={handleInputChange}
                  min="1"
                />
                <small>Quantidade total de andares (incluindo térreo)</small>
              </div>
              <div className="form-group">
                <label>Número de Unidades</label>
                <input
                  type="number"
                  name="numero_unidades"
                  placeholder="Ex: 12"
                  value={form.numero_unidades}
                  onChange={handleInputChange}
                  min="1"
                />
                <small>Quantidade de apartamentos, lojas ou salas comerciais</small>
              </div>
            </div>
          </section>

          {/* Seção 5: Compartimentos */}
          <section className="form-section">
            <h2>🚪 Compartimentos (Cômodos)</h2>
            <p className="section-description">Detalhes de cada cômodo: salas, quartos, cozinha, banheiro, etc. Mínimo de áreas e aberturas exigidas por lei</p>
            {compartimentos.map((comp, idx) => (
              <div key={idx} className="compartimento-card">
                <div className="compartimento-row">
                  <div className="form-group">
                    <label>Nome do Compartimento</label>
                    <input
                      type="text"
                      placeholder="Ex: Sala de estar, Quarto 1, Cozinha"
                      value={comp.nome}
                      onChange={(e) => handleCompartimentoChange(idx, 'nome', e.target.value)}
                    />
                    <small>Nome ou função do cômodo</small>
                  </div>
                  <div className="form-group">
                    <label>Área (m²)</label>
                    <input
                      type="number"
                      placeholder="Ex: 25.50"
                      value={comp.area}
                      onChange={(e) => handleCompartimentoChange(idx, 'area', e.target.value)}
                      step="0.01"
                    />
                    <small>Metragem quadrada do cômodo</small>
                  </div>
                  <div className="form-group">
                    <label>Pé-direito (m)</label>
                    <input
                      type="number"
                      placeholder="Ex: 2.70"
                      value={comp.pe_direito}
                      onChange={(e) => handleCompartimentoChange(idx, 'pe_direito', e.target.value)}
                      step="0.01"
                    />
                    <small>Altura do chão ao teto (mínimo 2.70m)</small>
                  </div>
                  <div className="form-group">
                    <label>Iluminação - Abertura (m²)</label>
                    <input
                      type="number"
                      placeholder="Ex: 5.00"
                      value={comp.iluminacao_abertura}
                      onChange={(e) => handleCompartimentoChange(idx, 'iluminacao_abertura', e.target.value)}
                      step="0.01"
                    />
                    <small>Área de janela para iluminação natural (≥1/6 área do cômodo)</small>
                  </div>
                  <div className="form-group">
                    <label>Ventilação - Abertura (m²)</label>
                    <input
                      type="number"
                      placeholder="Ex: 3.00"
                      value={comp.ventilacao_abertura}
                      onChange={(e) => handleCompartimentoChange(idx, 'ventilacao_abertura', e.target.value)}
                      step="0.01"
                    />
                    <small>Área de abertura para ventilação (≥1/8 área do cômodo)</small>
                  </div>
                </div>
                {compartimentos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removerCompartimento(idx)}
                    className="btn-remove"
                  >
                    ✕ Remover
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={adicionarCompartimento}
              className="btn-secondary"
            >
              + Adicionar Compartimento
            </button>
          </section>

          {/* Seção 6: Estacionamento e Acessibilidade */}
          <section className="form-section">
            <h2>🚗 Estacionamento & ♿ Acessibilidade</h2>
            <p className="section-description">Vagas de estacionamento e facilidades de acessibilidade para pessoas com deficiência</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Vagas de Estacionamento</label>
                <input
                  type="number"
                  name="vagas_estacionamento"
                  placeholder="Ex: 20"
                  value={form.vagas_estacionamento}
                  onChange={handleInputChange}
                  min="0"
                />
                <small>Total de vagas de estacionamento no projeto</small>
              </div>
              <div className="form-group">
                <label>Vagas PCD (Pessoa com Deficiência)</label>
                <input
                  type="number"
                  name="vagas_pcd"
                  placeholder="Ex: 2"
                  value={form.vagas_pcd}
                  onChange={handleInputChange}
                  min="0"
                />
                <small>Vagas destinadas a pessoas com deficiência ou mobilidade reduzida</small>
              </div>
            </div>
            <div className="accessibility-group">
              <h3>Medidas de Acessibilidade</h3>
              <p className="section-description">Marque os itens de acessibilidade presentes no projeto conforme NBR 9050</p>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="tem_rampa_acesso"
                    checked={form.tem_rampa_acesso}
                    onChange={handleInputChange}
                  />
                  <span>
                    <strong>Rampa de Acesso</strong>
                    <br />
                    <small>Acesso sem degraus para entrada principal com inclinação máxima 5%</small>
                  </span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="banheiro_pcd"
                    checked={form.banheiro_pcd}
                    onChange={handleInputChange}
                  />
                  <span>
                    <strong>Banheiro PCD</strong>
                    <br />
                    <small>Banheiro acessível com dimensões conforme NBR 9050 (espaço de manobra 1.50m)</small>
                  </span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="elevador"
                    checked={form.elevador}
                    onChange={handleInputChange}
                  />
                  <span>
                    <strong>Elevador</strong>
                    <br />
                    <small>Elevador acessível com dimensões mínimas 1.10m x 1.40m</small>
                  </span>
                </label>
              </div>
            </div>
          </section>

          {/* Botão de Envio */}
          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Analisando...' : '🚀 Analisar Projeto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AnalisarProjeto;
